<template>
  <q-card flat bordered>
    <q-card-section>
      <div class="section-header">
        <div class="text-h6">Basis of Quotation</div>
        <q-btn
          flat
          dense
          color="primary"
          icon="add"
          label="Add Item"
          :disable="loading"
          @click="addItem"
        />
      </div>

      <q-linear-progress v-if="loading" indeterminate color="primary" class="q-mt-md" />

      <q-list v-else-if="internalItems.length > 0" bordered separator class="q-mt-md">
        <q-item
          v-for="(item, index) in internalItems"
          :key="index"
        >
          <q-item-section avatar>
            <q-icon name="fiber_manual_record" size="8px" color="primary" />
          </q-item-section>

          <q-item-section>
            <q-input
              v-model="internalItems[index]"
              dense
              outlined
              type="textarea"
              autogrow
              maxlength="1000"
              counter
              :disable="loading"
              placeholder="Enter basis of quotation item..."
              @blur="saveItems"
            />
          </q-item-section>

          <q-item-section side>
            <div class="row q-gutter-xs">
              <q-btn
                flat
                round
                dense
                icon="arrow_upward"
                size="sm"
                :disable="index === 0 || loading"
                @click="moveItemUp(index)"
              >
                <q-tooltip>Move Up</q-tooltip>
              </q-btn>
              <q-btn
                flat
                round
                dense
                icon="arrow_downward"
                size="sm"
                :disable="index === internalItems.length - 1 || loading"
                @click="moveItemDown(index)"
              >
                <q-tooltip>Move Down</q-tooltip>
              </q-btn>
              <q-btn
                flat
                round
                dense
                icon="delete"
                size="sm"
                color="negative"
                :disable="loading"
                @click="confirmDeleteItem(index)"
              >
                <q-tooltip>Delete Item</q-tooltip>
              </q-btn>
            </div>
          </q-item-section>
        </q-item>
      </q-list>

      <div v-else class="empty-state q-pa-lg text-center">
        <q-icon name="list_alt" size="48px" color="grey-5" />
        <p class="text-grey-7 q-mt-md">No basis of quotation items yet. Click "Add Item" to get started.</p>
      </div>
    </q-card-section>
  </q-card>

  <!-- Delete Confirmation Dialog -->
  <q-dialog v-model="showDeleteDialog" persistent>
    <q-card>
      <q-card-section class="row items-center">
        <q-avatar icon="warning" color="negative" text-color="white" />
        <span class="q-ml-sm">Are you sure you want to delete this item?</span>
      </q-card-section>

      <q-card-actions align="right">
        <q-btn flat label="Cancel" @click="showDeleteDialog = false" />
        <q-btn
          flat
          label="Delete"
          color="negative"
          @click="deleteItem"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script setup>
// Cache-bust: 2025-10-31-14:30
  import { ref, watch } from 'vue';
  import { useQuasar } from 'quasar';
  import { authenticatedFetch } from '@/utils/apiHelper';

  const props = defineProps({
    tenderId: {
      type: Number,
      required: true,
    },
    items: {
      type: Array,
      default: () => [],
    },
  });

  const emit = defineEmits(['updated']);

  const $q = useQuasar();

  // State
  const loading = ref(false);
  const internalItems = ref([...props.items]);
  const showDeleteDialog = ref(false);
  const deleteIndex = ref(null);

  // Watch for prop changes
  watch(() => props.items, (newItems) => {
    internalItems.value = [...newItems];
  }, { deep: true });

  // Methods
  function addItem() {
    internalItems.value.push('');
  // Don't save immediately - let user type first
  // Save will happen on blur via @blur="saveItems" on the input
  }

  function moveItemUp(index) {
    if (index === 0) {return;}

    const items = [...internalItems.value];
    const temp = items[index];
    items[index] = items[index - 1];
    items[index - 1] = temp;

    internalItems.value = items;
    saveItems();
  }

  function moveItemDown(index) {
    if (index === internalItems.value.length - 1) {return;}

    const items = [...internalItems.value];
    const temp = items[index];
    items[index] = items[index + 1];
    items[index + 1] = temp;

    internalItems.value = items;
    saveItems();
  }

  function confirmDeleteItem(index) {
    deleteIndex.value = index;
    showDeleteDialog.value = true;
  }

  function deleteItem() {
    if (deleteIndex.value !== null) {
      internalItems.value.splice(deleteIndex.value, 1);
      saveItems();
    }
    showDeleteDialog.value = false;
    deleteIndex.value = null;
  }

  async function saveItems() {
    // Filter out empty items before saving
    const filteredItems = internalItems.value.filter((item) => item.trim() !== '');

    const url = `/api/tenders/${props.tenderId}/basis-of-quotation`;
    const payload = { basis_of_quotation: filteredItems };


    loading.value = true;
    try {
      const response = await authenticatedFetch(url, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      $q.notify({
        type: 'positive',
        message: 'Basis of quotation updated successfully',
        timeout: 2000,
      });

      emit('updated', data.data);
    } catch (error) {
      if (!error.isAuthError) {
        $q.notify({
          type: 'negative',
          message: 'Failed to save: ' + error.message,
          timeout: 4000,
        });
      }
    } finally {
      loading.value = false;
    }
  }
</script>

<style scoped>
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.empty-state {
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
</style>
